import React, { useState, useEffect, useContext } from 'react';
import RequestService from '../../services/RequestService';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import {
    Grid2 as Grid,
    Typography,
    Breadcrumbs,
    Link,
    Button,
    Box,
    Snackbar,
    Alert,
    useMediaQuery,
    TablePagination,
    Stack,
    Chip,
} from '@mui/material';
import { UserContext } from '../../contexts/UserContext';
import { useTheme } from '@mui/material/styles';

import TableComponent from '../../widgets/TableComponent';
import NewEventDialog from '../../widgets/events/NewEventDialog';
import EventList from '../../widgets/events/EventList';
import EditEventDialog from '../../widgets/events/EditEventDialog';
import DeleteEventDialog from '../../widgets/events/DeleteEventDialog';
import { formatDate } from '../../utils/formatDate';

const EventsPage = () => {
    const { user, hasPermission } = useContext(UserContext);

    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [totalElements, setTotalElements] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchEvents = async () => {
        try {
            setLoading(true);
            setError(null);

            const data = await RequestService.getEvents(user.company.id, {
                page,
                size: rowsPerPage,
            });

            setEvents(data.events || []);
            setTotalElements(data.totalElements ?? 0);
        } catch (err) {
            // console.log(err);
            setError('Erro ao carregar eventos.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents();
        // eslint-disable-next-line
    }, [page, rowsPerPage]);

    const handleRowClick = (event) => {
        if (hasPermission('EVENT_UPDATE')) {
            setSelectedEvent(event);
            setIsEditDialogOpen(true);
        }
    };

    const handleCloseEditDialog = () => {
        setIsEditDialogOpen(false);
        setSelectedEvent(null);
    };

    const handleOpenAddDialog = () => setIsAddDialogOpen(true);
    const handleCloseAddDialog = () => setIsAddDialogOpen(false);

    const handleEdit = (row) => handleRowClick(row);

    const handleOpenDeleteDialog = (row) => {
        setSelectedEvent(row);
        setIsDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setIsDeleteDialogOpen(false);
        setSelectedEvent(null);
    };

    const handleSuccessAdd = async (message) => {
        await fetchEvents();
        setSnackbarMessage(message || 'Evento criado com sucesso!');
        setSnackbarOpen(true);
    };

    const handleSuccessEdit = async (message) => {
        await fetchEvents();
        setSnackbarMessage(message || 'Evento atualizado com sucesso!');
        setSnackbarOpen(true);
    };

    const handleSuccessDelete = async () => {
        await fetchEvents();
        setSnackbarMessage('Evento excluído com sucesso!');
        setSnackbarOpen(true);
    };

    const handleDeleteEvent = async (eventId) => {
        await RequestService.deleteEvent(eventId);
        await fetchEvents();

        setSnackbarMessage('Evento excluído com sucesso!');
        setSnackbarOpen(true);

        // fecha dialog e limpa seleção
        setIsDeleteDialogOpen(false);
        setSelectedEvent(null);

        // se estava na página de edição aberta pro mesmo evento, fecha também
        setIsEditDialogOpen(false);
    };

    const handleSnackbarClose = () => setSnackbarOpen(false);

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const StatusChip = ({ active }) => (
        <Chip
            label={active ? 'Ativo' : 'Inativo'}
            size='small'
            color={active ? 'success' : 'default'}
            variant={active ? 'filled' : 'outlined'}
        />
    );

    const columns = [
        {
            label: 'Título',
            field: 'title',
            align: 'left',
            renderCell: (value, row) => (
                <Stack direction='column' spacing={0.5}>
                    <Typography variant='body2' sx={{ color: '#292929' }}>
                        {row.title}
                    </Typography>
                    <Typography variant='caption' sx={{ color: '#777' }}>
                        {row.description?.slice(0, 90) || '-'}
                        {row.description?.length > 90 ? '...' : ''}
                    </Typography>
                </Stack>
            ),
        },
        {
            label: 'Período',
            field: 'startsAt',
            align: 'center',
            renderCell: (value, row) => (
                <Stack
                    direction='column'
                    spacing={0.5}
                    sx={{ alignItems: 'center' }}
                >
                    <Typography
                        variant='body2'
                        sx={{ fontSize: '14px', color: '#292929' }}
                    >
                        Início: {formatDate(row.startsAt)}
                    </Typography>
                    <Typography
                        variant='body2'
                        sx={{ fontSize: '14px', color: '#292929' }}
                    >
                        Fim: {formatDate(row.endsAt)}
                    </Typography>
                </Stack>
            ),
        },
        {
            label: 'Status',
            field: 'active',
            align: 'center',
            renderCell: (value, row) => <StatusChip active={!!row.active} />,
        },
        {
            label: '',
            field: 'actions',
            align: 'right',
            renderCell: (value, row) => (
                <Stack direction='row' spacing={1} justifyContent='flex-end'>
                    {hasPermission('EVENT_UPDATE') && (
                        <Button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(row);
                            }}
                            size='small'
                        >
                            Editar
                        </Button>
                    )}
                    {hasPermission('EVENT_DELETE') && (
                        <Button
                            onClick={() => handleOpenDeleteDialog(row)}
                            size='small'
                            color='error'
                        >
                            Excluir
                        </Button>
                    )}
                </Stack>
            ),
        },
    ];

    if (loading) return <div>Carregando...</div>;
    if (error) return <div>{error}</div>;

    const renderEmptyState = () => (
        <Box
            sx={{
                textAlign: 'center',
                mt: 4,
                color: '#666',
                fontFamily: 'Poppins',
                fontSize: 18,
            }}
        >
            <Typography variant='h6'>Nenhum evento cadastrado.</Typography>
            <Typography variant='body2'>
                Clique em <strong>"Novo Evento"</strong> para adicionar o
                primeiro!
            </Typography>
        </Box>
    );

    const renderMobileLayout = () => {
        return events.length === 0 ? (
            renderEmptyState()
        ) : (
            <EventList
                events={events}
                shouldEdit={hasPermission('EVENT_UPDATE')}
                shouldDelete={hasPermission('EVENT_DELETE')}
                onClick={handleRowClick}
                onDeleteClick={handleOpenDeleteDialog}
            />
        );
    };

    const renderDesktopLayout = () => {
        if (events.length === 0) return renderEmptyState();

        return (
            <Box>
                <TableComponent columns={columns} rows={events} />

                <TablePagination
                    component='div'
                    count={totalElements}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                />
            </Box>
        );
    };

    return (
        <Box>
            <Grid container justifyContent='start'>
                <Breadcrumbs aria-label='breadcrumb'>
                    <Link underline='hover' color='inherit' href='/'>
                        Início
                    </Link>
                    <Link underline='hover' color='inherit'>
                        Definições
                    </Link>
                    <Typography sx={{ color: 'text.primary' }}>
                        Eventos
                    </Typography>
                </Breadcrumbs>
            </Grid>

            <Grid container xs={12} sx={{ mt: 3 }}>
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: 24,
                        fontWeight: '500',
                        color: '#0C3466',
                        mb: 2,
                    }}
                >
                    Eventos
                </Typography>
            </Grid>

            <Grid container sx={12} justifyContent={'end'}>
                <Grid item sx={6}>
                    {hasPermission('EVENT_CREATE') && (
                        <Button
                            variant='contained'
                            sx={{ minHeight: '47px', mb: 2 }}
                            endIcon={<AddCircleOutlineOutlinedIcon />}
                            onClick={handleOpenAddDialog}
                        >
                            Novo Evento
                        </Button>
                    )}
                </Grid>
            </Grid>

            {isMobile ? renderMobileLayout() : renderDesktopLayout()}

            <NewEventDialog
                open={isAddDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') handleCloseAddDialog();
                }}
                onSuccess={handleSuccessAdd}
                user={user}
            />

            <EditEventDialog
                open={isEditDialogOpen}
                onClose={handleCloseEditDialog}
                onSuccess={handleSuccessEdit}
                user={user}
                event={selectedEvent}
            />

            <DeleteEventDialog
                open={isDeleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                event={selectedEvent}
                onConfirm={handleDeleteEvent}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity='success'
                    variant='filled'
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default EventsPage;
